import { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

/**
 * Custom hook that encapsulates all invoice generation, email sending,
 * and stamp confirmation logic for the AddEditLoadDialog.
 */
export function useInvoiceGeneration({ formData, setFormData, fileSetters, fileRefs }) {
  const {
    setInvoiceFile, setInvoiceFile1, setInvoiceFile2,
    setUpdatedInvoiceFile, setAdditionInvoiceFile, setDeductionInvoiceFile
  } = fileSetters;

  const { invoiceFileRef, invoiceFile1Ref, invoiceFile2Ref } = fileRefs;

  // Generation loading states
  const [generating, setGenerating] = useState(false);
  const [generating1, setGenerating1] = useState(false);
  const [generating2, setGenerating2] = useState(false);
  const [generatingUpdated, setGeneratingUpdated] = useState(false);
  const [generatingAddition, setGeneratingAddition] = useState(false);
  const [generatingDeduction, setGeneratingDeduction] = useState(false);

  // Manual invoice validation errors (keyed by field name)
  const [manualInvoiceErrors, setManualInvoiceErrors] = useState({});

  // Stamp confirmation dialogs
  const [showStampConfirm, setShowStampConfirm] = useState(false);
  const [showStampConfirm1, setShowStampConfirm1] = useState(false);
  const [showStampConfirm2, setShowStampConfirm2] = useState(false);
  const [showStampConfirmUpdated, setShowStampConfirmUpdated] = useState(false);
  const [showStampConfirmAddition, setShowStampConfirmAddition] = useState(false);
  const [showStampConfirmDeduction, setShowStampConfirmDeduction] = useState(false);

  // Validate manual invoice number against backend
  // Returns { valid: boolean, error?: string }
  const validateManualInvoice = useCallback(async (invoiceNumber, fieldKey) => {
    if (!invoiceNumber || !invoiceNumber.trim()) {
      setManualInvoiceErrors(prev => ({ ...prev, [fieldKey]: null }));
      return { valid: true };
    }
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/invoice/validate-manual-number`, {
        invoice_number: invoiceNumber.trim(),
        date_sent: formData.date_sent || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.valid) {
        setManualInvoiceErrors(prev => ({ ...prev, [fieldKey]: null }));
        return { valid: true };
      } else {
        setManualInvoiceErrors(prev => ({ ...prev, [fieldKey]: response.data.error }));
        return { valid: false, error: response.data.error };
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Validation failed';
      setManualInvoiceErrors(prev => ({ ...prev, [fieldKey]: msg }));
      return { valid: false, error: msg };
    }
  }, [formData.date_sent]);

  // Clear a manual invoice error
  const clearManualInvoiceError = useCallback((fieldKey) => {
    setManualInvoiceErrors(prev => ({ ...prev, [fieldKey]: null }));
  }, []);

  // Peek next invoice number
  const [peekedNumber, setPeekedNumber] = useState(null);
  const [peeking, setPeeking] = useState(false);
  const peekNextInvoiceNumber = useCallback(async () => {
    setPeeking(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/invoice/peek-next-number`, {
        params: { date_sent: formData.date_sent || undefined },
        headers: { Authorization: `Bearer ${token}` }
      });
      setPeekedNumber(res.data.next_number);
      // Auto-clear after 5s
      setTimeout(() => setPeekedNumber(null), 5000);
    } catch {
      setPeekedNumber('--');
    } finally {
      setPeeking(false);
    }
  }, [formData.date_sent]);

  // Email dialog state
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailInvoiceData, setEmailInvoiceData] = useState(null);
  const [emailAddress, setEmailAddress] = useState('');

  // Helper: create PDF blob from base64
  const createPdfBlob = (base64Data) => {
    if (!base64Data) return { blob: null, url: null };
    const blob = new Blob(
      [Uint8Array.from(atob(base64Data), c => c.charCodeAt(0))],
      { type: 'application/pdf' }
    );
    return { blob, url: URL.createObjectURL(blob) };
  };

  // ─── Single Invoice ───────────────────────────────────────────
  const handleGenerateInvoiceClick = async () => {
    if (!formData.product || !formData.kgs || !formData.rate) {
      toast.error('Please fill in Product, Kgs, and Rate before generating invoice');
      return;
    }
    if (!formData.client_name) {
      toast.error('Please select a client before generating invoice');
      return;
    }
    // Validate manual invoice number if provided
    if (formData.manual_invoice_number?.trim()) {
      const result = await validateManualInvoice(formData.manual_invoice_number, 'manual_invoice_number');
      if (!result.valid) {
        toast.error(result.error || 'Invalid manual invoice number');
        return;
      }
    }
    setShowStampConfirm(true);
  };

  const handleGenerateInvoice = async (includeStamp) => {
    setShowStampConfirm(false);
    setGenerating(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/invoice/generate`, {
        client_name: formData.client_name, product: formData.product,
        kgs: parseFloat(formData.kgs), rate: parseFloat(formData.rate),
        weight_status: formData.weight_status || null, date_sent: formData.date_sent || null,
        po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
        include_stamp: includeStamp, load_id: null, is_updated_invoice: false, preview: true,
        manual_invoice_number: formData.manual_invoice_number ?? ''
      }, { headers: { Authorization: `Bearer ${token}` } });

      const data = response.data;
      const { blob: pdfBlob, url: pdfUrl } = createPdfBlob(data.invoice_pdf_base64);
      const displayInvoiceNumber = data.invoice_number.replace('PREVIEW-', '');

      setFormData(prev => ({
        ...prev,
        generated_invoice_number: displayInvoiceNumber,
        generated_invoice_amount: data.invoice_amount.toFixed(2),
        generated_invoice_file: pdfUrl || data.invoice_file_url,
        invoice_number: displayInvoiceNumber,
        invoice_amount: data.invoice_amount.toFixed(2),
        _preview_invoice_data: {
          client_name: formData.client_name, product: formData.product,
          kgs: parseFloat(formData.kgs), rate: parseFloat(formData.rate),
          weight_status: formData.weight_status || null, date_sent: formData.date_sent || null,
          po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
          include_stamp: includeStamp, is_updated_invoice: false,
          manual_invoice_number: displayInvoiceNumber
        }
      }));

      if (pdfBlob) {
        setInvoiceFile(new File([pdfBlob], `${displayInvoiceNumber}.pdf`, { type: 'application/pdf' }));
        if (invoiceFileRef?.current) invoiceFileRef.current.value = '';
      }
      toast.success(`Invoice ${displayInvoiceNumber} generated (preview)!`);
    } catch (error) {
      console.error('Invoice generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  // ─── BALAJI Invoice 1 ─────────────────────────────────────────
  const handleGenerateInvoice1Click = async () => {
    if (!formData.product || !formData.kgs_1 || !formData.rate) {
      toast.error('Please fill in Product, Kgs for Invoice 1, and Rate'); return;
    }
    if (!formData.client_name) { toast.error('Please select a client before generating invoice'); return; }
    // Validate manual invoice number if provided
    if (formData.manual_invoice_number_1?.trim()) {
      const result = await validateManualInvoice(formData.manual_invoice_number_1, 'manual_invoice_number_1');
      if (!result.valid) {
        toast.error(result.error || 'Invalid manual invoice number for Invoice 1');
        return;
      }
    }
    setShowStampConfirm1(true);
  };

  const handleGenerateInvoice1 = async (includeStamp) => {
    setShowStampConfirm1(false);
    setGenerating1(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/invoice/generate`, {
        client_name: formData.client_name, product: formData.product,
        kgs: parseFloat(formData.kgs_1), rate: parseFloat(formData.rate),
        weight_status: formData.weight_status || null, date_sent: formData.date_sent || null,
        po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
        include_stamp: includeStamp, load_id: null, is_updated_invoice: false, preview: true, invoice_index: 1,
        manual_invoice_number: formData.manual_invoice_number_1 ?? ''
      }, { headers: { Authorization: `Bearer ${token}` } });

      const data = response.data;
      const { blob: pdfBlob, url: pdfUrl } = createPdfBlob(data.invoice_pdf_base64);
      const displayInvoiceNumber = data.invoice_number.replace('PREVIEW-', '');

      setFormData(prev => ({
        ...prev,
        generated_invoice_number_1: displayInvoiceNumber,
        generated_invoice_amount_1: data.invoice_amount.toFixed(2),
        generated_invoice_file_1: pdfUrl || data.invoice_file_url,
        generated_invoice_number: displayInvoiceNumber,
        invoice_number: displayInvoiceNumber,
        _preview_invoice_data_1: {
          client_name: formData.client_name, product: formData.product,
          kgs: parseFloat(formData.kgs_1), rate: parseFloat(formData.rate),
          weight_status: formData.weight_status || null, date_sent: formData.date_sent || null,
          po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
          include_stamp: includeStamp, is_updated_invoice: false, invoice_index: 1,
          manual_invoice_number: displayInvoiceNumber
        }
      }));

      if (pdfBlob) {
        setInvoiceFile1(new File([pdfBlob], `${displayInvoiceNumber}.pdf`, { type: 'application/pdf' }));
        if (invoiceFile1Ref?.current) invoiceFile1Ref.current.value = '';
      }
      toast.success(`Invoice 1: ${displayInvoiceNumber} generated (preview)!`);
    } catch (error) {
      console.error('Invoice 1 generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate Invoice 1');
    } finally { setGenerating1(false); }
  };

  // ─── BALAJI Invoice 2 ─────────────────────────────────────────
  const handleGenerateInvoice2Click = async () => {
    if (!formData.product || !formData.kgs_2 || !formData.rate) {
      toast.error('Please fill in Product, Kgs for Invoice 2, and Rate'); return;
    }
    if (!formData.client_name) { toast.error('Please select a client before generating invoice'); return; }
    if (!formData.generated_invoice_number_1) { toast.error('Please generate Invoice 1 first'); return; }
    // Validate manual invoice number if provided
    if (formData.manual_invoice_number_2?.trim()) {
      const result = await validateManualInvoice(formData.manual_invoice_number_2, 'manual_invoice_number_2');
      if (!result.valid) {
        toast.error(result.error || 'Invalid manual invoice number for Invoice 2');
        return;
      }
    }
    setShowStampConfirm2(true);
  };

  const handleGenerateInvoice2 = async (includeStamp) => {
    setShowStampConfirm2(false);
    setGenerating2(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/invoice/generate`, {
        client_name: formData.client_name, product: formData.product,
        kgs: parseFloat(formData.kgs_2), rate: parseFloat(formData.rate),
        weight_status: formData.weight_status || null, date_sent: formData.date_sent || null,
        po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
        include_stamp: includeStamp, load_id: null, is_updated_invoice: false, preview: true,
        invoice_index: 2, base_invoice_number: formData.generated_invoice_number_1,
        manual_invoice_number: formData.manual_invoice_number_2 ?? ''
      }, { headers: { Authorization: `Bearer ${token}` } });

      const data = response.data;
      const { blob: pdfBlob, url: pdfUrl } = createPdfBlob(data.invoice_pdf_base64);
      const displayInvoiceNumber = data.invoice_number.replace('PREVIEW-', '');

      setFormData(prev => ({
        ...prev,
        generated_invoice_number_2: displayInvoiceNumber,
        generated_invoice_amount_2: data.invoice_amount.toFixed(2),
        generated_invoice_file_2: pdfUrl || data.invoice_file_url,
        _preview_invoice_data_2: {
          client_name: formData.client_name, product: formData.product,
          kgs: parseFloat(formData.kgs_2), rate: parseFloat(formData.rate),
          weight_status: formData.weight_status || null, date_sent: formData.date_sent || null,
          po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
          include_stamp: includeStamp, is_updated_invoice: false, invoice_index: 2,
          base_invoice_number: formData.generated_invoice_number_1,
          manual_invoice_number: displayInvoiceNumber
        }
      }));

      if (pdfBlob) {
        setInvoiceFile2(new File([pdfBlob], `${displayInvoiceNumber}.pdf`, { type: 'application/pdf' }));
        if (invoiceFile2Ref?.current) invoiceFile2Ref.current.value = '';
      }
      toast.success(`Invoice 2: ${displayInvoiceNumber} generated (preview)!`);
    } catch (error) {
      console.error('Invoice 2 generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate Invoice 2');
    } finally { setGenerating2(false); }
  };

  // ─── Updated Invoice (BALAJI) ─────────────────────────────────
  const handleGenerateUpdatedInvoiceClick = () => {
    if (!formData.total_kgs_updated || !formData.total_tons_updated) {
      toast.error('Please fill in Total Kgs and Total Tons before generating updated invoice'); return;
    }
    if (!formData.rate) { toast.error('Please fill in Rate in Invoice Details section first'); return; }
    setShowStampConfirmUpdated(true);
  };

  const handleGenerateUpdatedInvoice = async (includeStamp) => {
    setShowStampConfirmUpdated(false);
    setGeneratingUpdated(true);
    const token = localStorage.getItem('token');
    try {
      const kgs = parseFloat(formData.total_kgs_updated);
      const rate = parseFloat(formData.rate);

      const response = await axios.post(`${API}/invoice/generate`, {
        client_name: formData.client_name, product: formData.product || 'Silica sand dry',
        kgs, rate, weight_status: null, date_sent: formData.date_sent || null,
        po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
        include_stamp: includeStamp, load_id: null, is_updated_invoice: true, preview: true,
        manual_invoice_number: formData.generated_updated_invoice_number || formData.generated_invoice_number_2 || formData.generated_invoice_number_1 || formData.generated_invoice_number || ''
      }, { headers: { Authorization: `Bearer ${token}` } });

      const data = response.data;
      const { blob: pdfBlob, url: pdfUrl } = createPdfBlob(data.invoice_pdf_base64);
      const displayInvoiceNumber = data.invoice_number.replace('PREVIEW-', '');

      setFormData(prev => ({
        ...prev,
        generated_updated_invoice_number: displayInvoiceNumber,
        generated_updated_invoice_amount: data.invoice_amount.toFixed(2),
        generated_updated_invoice_file: pdfUrl || data.invoice_file_url,
        updated_invoice_number: displayInvoiceNumber,
        updated_invoice_amount: data.invoice_amount.toFixed(2),
        final_invoice_amount: data.invoice_amount.toFixed(2),
        _preview_updated_invoice_data: {
          client_name: formData.client_name, product: formData.product || 'Silica sand dry',
          kgs, rate, weight_status: null, date_sent: formData.date_sent || null,
          po_number: formData.purchase_order || null, vehicle_number: formData.vehicle_number || null,
          include_stamp: includeStamp, is_updated_invoice: true,
          manual_invoice_number: displayInvoiceNumber
        }
      }));

      if (pdfBlob) {
        setUpdatedInvoiceFile(new File([pdfBlob], `${displayInvoiceNumber}_updated.pdf`, { type: 'application/pdf' }));
      }
      // Toast removed - auto scroll and button blink will guide user
    } catch (error) {
      console.error('Updated invoice generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate updated invoice');
    } finally { setGeneratingUpdated(false); }
  };

  // ─── Addition Invoice (SVE) ───────────────────────────────────
  const handleGenerateAdditionInvoiceClick = () => {
    if (!formData.additional_kgs || !formData.rate) {
      toast.error('Please fill in Additional Kgs and Rate before generating addition invoice'); return;
    }
    if (!formData.invoice_number && !formData.generated_invoice_number) {
      toast.error('Original invoice must be generated first before creating addition invoice'); return;
    }
    setShowStampConfirmAddition(true);
  };

  const handleGenerateAdditionInvoice = async (includeStamp) => {
    setShowStampConfirmAddition(false);
    setGeneratingAddition(true);
    const token = localStorage.getItem('token');
    try {
      const kgs = parseFloat(formData.additional_kgs);
      const rate = parseFloat(formData.rate);
      const baseInvoiceNumber = formData.invoice_number || formData.generated_invoice_number;

      const response = await axios.post(`${API}/invoice/generate-addition`, {
        client_name: formData.client_name, product: formData.product || 'Silica sand dry',
        kgs, rate, base_invoice_number: baseInvoiceNumber,
        date_sent: formData.date_sent || null, po_number: formData.purchase_order || null,
        vehicle_number: formData.vehicle_number || null, include_stamp: includeStamp,
        load_id: null, preview: true
      }, { headers: { Authorization: `Bearer ${token}` } });

      const data = response.data;
      const { blob: pdfBlob, url: pdfUrl } = createPdfBlob(data.invoice_pdf_base64);
      const displayInvoiceNumber = data.invoice_number.replace('PREVIEW-', '');

      setFormData(prev => ({
        ...prev,
        generated_addition_invoice_number: displayInvoiceNumber,
        generated_addition_invoice_amount: data.invoice_amount.toFixed(2),
        generated_addition_invoice_file: pdfUrl || data.invoice_file_url,
        addition_invoice_number: displayInvoiceNumber,
        additional_invoice_amount: data.invoice_amount.toFixed(2),
        _preview_addition_invoice_data: {
          client_name: formData.client_name, product: formData.product || 'Silica sand dry',
          kgs, rate, base_invoice_number: baseInvoiceNumber,
          date_sent: formData.date_sent || null, po_number: formData.purchase_order || null,
          vehicle_number: formData.vehicle_number || null, include_stamp: includeStamp,
          manual_invoice_number: displayInvoiceNumber
        }
      }));

      if (pdfBlob) {
        setAdditionInvoiceFile(new File([pdfBlob], `${displayInvoiceNumber}_addition.pdf`, { type: 'application/pdf' }));
      }
      toast.success(`Addition Invoice ${displayInvoiceNumber} generated (preview)!`);
    } catch (error) {
      console.error('Addition invoice generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate addition invoice');
    } finally { setGeneratingAddition(false); }
  };

  // ─── Deduction Invoice (SVE) ──────────────────────────────────
  const handleGenerateDeductionInvoiceClick = () => {
    if (!formData.deduction_kgs || !formData.rate) {
      toast.error('Please fill in Deduction Kgs and Rate before generating deduction invoice'); return;
    }
    if (!formData.invoice_number && !formData.generated_invoice_number) {
      toast.error('Original invoice must be generated first before creating deduction invoice'); return;
    }
    setShowStampConfirmDeduction(true);
  };

  const handleGenerateDeductionInvoice = async (includeStamp) => {
    setShowStampConfirmDeduction(false);
    setGeneratingDeduction(true);
    const token = localStorage.getItem('token');
    try {
      const kgs = parseFloat(formData.deduction_kgs);
      const rate = parseFloat(formData.rate);
      const baseInvoiceNumber = formData.invoice_number || formData.generated_invoice_number;

      const response = await axios.post(`${API}/invoice/generate-deduction`, {
        client_name: formData.client_name, product: formData.product || 'Silica sand dry',
        kgs, rate, base_invoice_number: baseInvoiceNumber,
        date_sent: formData.date_sent || null, po_number: formData.purchase_order || null,
        vehicle_number: formData.vehicle_number || null, include_stamp: includeStamp,
        load_id: null, preview: true
      }, { headers: { Authorization: `Bearer ${token}` } });

      const data = response.data;
      const { blob: pdfBlob, url: pdfUrl } = createPdfBlob(data.invoice_pdf_base64);
      const displayInvoiceNumber = data.invoice_number.replace('PREVIEW-', '');
      
      // Calculate final invoice amount = original invoice amount - deduction amount
      const originalInvoiceAmount = parseFloat(formData.invoice_amount || formData.generated_invoice_amount || 0);
      const deductionAmount = parseFloat(data.invoice_amount);
      const finalAmount = (originalInvoiceAmount - deductionAmount).toFixed(2);

      setFormData(prev => ({
        ...prev,
        generated_deduction_invoice_number: displayInvoiceNumber,
        generated_deduction_invoice_amount: data.invoice_amount.toFixed(2),
        generated_deduction_invoice_file: pdfUrl || data.invoice_file_url,
        deduction_invoice_number: displayInvoiceNumber,
        deduction_invoice_amount: data.invoice_amount.toFixed(2),
        final_invoice_amount: finalAmount,
        _preview_deduction_invoice_data: {
          client_name: formData.client_name, product: formData.product || 'Silica sand dry',
          kgs, rate, base_invoice_number: baseInvoiceNumber,
          date_sent: formData.date_sent || null, po_number: formData.purchase_order || null,
          vehicle_number: formData.vehicle_number || null, include_stamp: includeStamp,
          manual_invoice_number: displayInvoiceNumber
        }
      }));

      if (pdfBlob) {
        setDeductionInvoiceFile(new File([pdfBlob], `${displayInvoiceNumber}_deduction.pdf`, { type: 'application/pdf' }));
      }
      toast.success(`Deduction Invoice ${displayInvoiceNumber} generated (preview)!`);
    } catch (error) {
      console.error('Deduction invoice generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate deduction invoice');
    } finally { setGeneratingDeduction(false); }
  };

  // ─── Email ────────────────────────────────────────────────────
  const handleSendEmailClick = (invoiceNumber, invoiceAmount, invoiceType) => {
    const clientDetails = {
      'SVE CASTINGS PVT LTD': 'accounts@svecastings.com',
      'BALAJI CASTINGS PVT LTD': 'accounts@balajicastings.com'
    };
    setEmailAddress(clientDetails[formData.client_name] || '');
    setEmailInvoiceData({
      invoice_number: invoiceNumber,
      invoice_amount: parseFloat(invoiceAmount),
      invoice_type: invoiceType,
      client_name: formData.client_name
    });
    setShowEmailDialog(true);
  };

  const handleSendEmail = async () => {
    if (!emailAddress || !emailInvoiceData) { toast.error('Please enter an email address'); return; }
    setSendingEmail(true);
    const token = localStorage.getItem('token');
    try {
      await axios.post(`${API}/invoice/send-email`, {
        invoice_number: emailInvoiceData.invoice_number,
        to_email: emailAddress,
        client_name: emailInvoiceData.client_name,
        invoice_amount: emailInvoiceData.invoice_amount,
        invoice_type: emailInvoiceData.invoice_type
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invoice sent to ${emailAddress}`);
      setShowEmailDialog(false);
      setEmailAddress('');
      setEmailInvoiceData(null);
    } catch (error) {
      console.error('Email send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally { setSendingEmail(false); }
  };

  return {
    // Generation states
    generating, generating1, generating2, generatingUpdated, generatingAddition, generatingDeduction,
    // Manual invoice validation
    manualInvoiceErrors, validateManualInvoice, clearManualInvoiceError,
    // Peek next invoice number
    peekedNumber, peeking, peekNextInvoiceNumber,
    // Stamp confirm states
    showStampConfirm, showStampConfirm1, showStampConfirm2,
    showStampConfirmUpdated, showStampConfirmAddition, showStampConfirmDeduction,
    setShowStampConfirm, setShowStampConfirm1, setShowStampConfirm2,
    setShowStampConfirmUpdated, setShowStampConfirmAddition, setShowStampConfirmDeduction,
    // Email states
    sendingEmail, showEmailDialog, emailInvoiceData, emailAddress,
    setShowEmailDialog, setEmailAddress, setEmailInvoiceData,
    // Handlers
    handleGenerateInvoiceClick, handleGenerateInvoice,
    handleGenerateInvoice1Click, handleGenerateInvoice1,
    handleGenerateInvoice2Click, handleGenerateInvoice2,
    handleGenerateUpdatedInvoiceClick, handleGenerateUpdatedInvoice,
    handleGenerateAdditionInvoiceClick, handleGenerateAdditionInvoice,
    handleGenerateDeductionInvoiceClick, handleGenerateDeductionInvoice,
    handleSendEmailClick, handleSendEmail,
  };
}
